import type { UIMessage } from 'ai'

import { requireSessionToken } from '@avantstay/auth-token'

// Remy web chat gateway. Default to prod; deployed builds inject the real URL via
// REACT_APP_TABLEAU_REMY_API (voyage's Vite config exposes process.env.REACT_APP_*, same
// pattern as the revenue chat). The session endpoints share this base path.
export const REMY_API =
  process.env.REACT_APP_TABLEAU_REMY_API ?? 'https://remy.prod.avantstay.com/api/remy/chat'

// A previous Remy conversation (one LangGraph thread). `externalId` is the chat
// `id` we send back to continue it. See remy/docs/remy-web-sessions.md.
export interface RemySession {
  externalId: string
  title: string | null
  turnCount: number
  createdAt: string
  lastActivityAt: string
}

// Onboarding status returned alongside the session list. When `required`, the
// user has not connected the Voyage connector to Remy yet — chat turns would be
// rejected — and `connectUrl` opens the one-time connect flow.
export interface RemyOnboarding {
  required: boolean
  connectUrl?: string
}

export interface RemySessionList {
  sessions: RemySession[]
  onboarding: RemyOnboarding
}

const authHeaders = async (): Promise<Record<string, string>> => {
  // The session token the chat stream uses; never sent without one.
  const token = await requireSessionToken()
  return { Authorization: `Bearer ${token}` }
}

// Newest-first list of the caller's web sessions (scoped server-side to the
// authenticated user — never another user's, never Slack threads). Sorted and
// capped client-side to `limit` regardless of how the server pages. Also
// carries the caller's onboarding status so the panel can gate chatting on the
// Voyage connector being connected.
export const fetchRemySessions = async (limit = 3): Promise<RemySessionList> => {
  const res = await fetch(`${REMY_API}/sessions?limit=${limit}`, {
    headers: await authHeaders(),
  })
  if (!res.ok) throw new Error(`Couldn't load previous chats (${res.status})`)
  const data: { sessions?: RemySession[]; onboarding?: RemyOnboarding } = await res.json()
  return {
    sessions: (data.sessions ?? [])
      .slice()
      .sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt))
      .slice(0, limit),
    // Older Remy deployments omit `onboarding`; treat that as onboarded.
    onboarding: data.onboarding ?? { required: false },
  }
}

// Conversation history for one session, already shaped as AI-SDK UI messages so
// it can seed `useChat`. Unknown/foreign session → empty list.
export const fetchRemySessionMessages = async (externalId: string): Promise<UIMessage[]> => {
  const res = await fetch(`${REMY_API}/sessions/${encodeURIComponent(externalId)}/messages`, {
    headers: await authHeaders(),
  })
  if (!res.ok) throw new Error(`Couldn't load that conversation (${res.status})`)
  const data: { messages?: UIMessage[] } = await res.json()
  return data.messages ?? []
}

export type RemyFeedbackRating = 'positive' | 'negative'

// Helpful/not-helpful rating of one assistant message. The server resolves the
// caller from the bearer token; the ids let feedback analysis find the exact
// conversation. `executionId` only exists for answers received live in this
// mount (it arrives as a `data-remy-execution` stream part) — resumed history
// falls back to chatId + messageId.
export const sendRemyFeedback = async (feedback: {
  rating: RemyFeedbackRating
  chatId?: string
  messageId?: string
  executionId?: string
}): Promise<void> => {
  const res = await fetch(`${REMY_API}/feedback`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(feedback),
  })
  if (!res.ok) throw new Error(`Couldn't send feedback (${res.status})`)
}

// The Remy user guide as raw markdown — the gateway serves its own
// USER_GUIDE.md, so the guide always matches the deployed Remy version and
// Voyage never bundles a copy. Cached for the page lifetime; it only changes
// with Remy deploys.
let cachedGuide: string | null = null
export const fetchRemyGuide = async (): Promise<string> => {
  if (cachedGuide !== null) return cachedGuide
  const res = await fetch(`${REMY_API}/guide`, { headers: await authHeaders() })
  if (!res.ok) throw new Error(`Couldn't load the guide (${res.status})`)
  cachedGuide = await res.text()
  return cachedGuide
}
